import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Save } from 'lucide-react';

const TEAM_TYPES = [
  { value: 'PARTICIPANT_TEAM', label: 'Participant Team' },
  { value: 'SIL_HOUSE_TEAM', label: 'SIL House Team' },
  { value: 'ACCOMMODATION_TEAM', label: 'Accommodation Team' },
  { value: 'SUPPORT_COORDINATION', label: 'Support Coordination Team' },
  { value: 'CLINICAL_TEAM', label: 'Clinical Team' },
  { value: 'ROSTERING_TEAM', label: 'Rostering Team' },
  { value: 'CUSTOM', label: 'Custom Team' },
];

export default function TeamFormPage() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const isEdit = teamId && teamId !== 'new';
  const [saving, setSaving] = useState(false);
  const [staff, setStaff] = useState([]);
  const [houses, setHouses] = useState([]);
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);

  const [form, setForm] = useState({
    team_name: '', team_code: '', team_type: 'PARTICIPANT_TEAM',
    description: '', state_id: '', branch_id: '',
    leader_staff_id: '', linked_house_id: '',
    shift_notifications: true, participant_notifications: true,
    house_notifications: true, messaging_enabled: true,
  });

  useEffect(() => {
    Promise.allSettled([
      api.get('/staff?pageSize=100'),
      api.get('/houses?pageSize=100'),
      api.get('/org/states'),
      api.get('/org/branches'),
    ]).then(([sR, hR, stR, brR]) => {
      if (sR.status === 'fulfilled') setStaff(sR.value.data.items || sR.value.data.data || []);
      if (hR.status === 'fulfilled') setHouses(hR.value.data.items || hR.value.data.data || []);
      if (stR.status === 'fulfilled') setStates(stR.value.data.data || []);
      if (brR.status === 'fulfilled') setBranches(brR.value.data.data || []);
    });
  }, []);

  useEffect(() => {
    if (isEdit) {
      api.get(`/teams/${teamId}`).then(({ data }) => {
        const t = data.data;
        setForm({
          team_name: t.team_name || '', team_code: t.team_code || '',
          team_type: t.team_type || 'PARTICIPANT_TEAM',
          description: t.description || '',
          state_id: t.state_id || '', branch_id: t.branch_id || '',
          leader_staff_id: t.leader_staff_id || '',
          linked_house_id: t.linked_house_id || '',
          shift_notifications: t.shift_notifications ?? true,
          participant_notifications: t.participant_notifications ?? true,
          house_notifications: t.house_notifications ?? true,
          messaging_enabled: t.messaging_enabled ?? true,
        });
      }).catch(() => toast.error('Failed to load team'));
    }
  }, [isEdit, teamId]);

  const handleSave = async () => {
    if (!form.team_name.trim()) { toast.error('Team name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.leader_staff_id) delete payload.leader_staff_id;
      if (!payload.linked_house_id) delete payload.linked_house_id;
      if (!payload.state_id) delete payload.state_id;
      if (!payload.branch_id) delete payload.branch_id;

      if (isEdit) {
        await api.put(`/teams/${teamId}`, payload);
        toast.success('Team updated');
      } else {
        const { data } = await api.post('/teams', payload);
        toast.success('Team created');
        navigate(`/teams/${data.data.id}`, { replace: true });
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="team-form-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/teams')} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <h1 className="text-2xl font-bold tracking-tight">{isEdit ? 'Edit Team' : 'Create Team'}</h1>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-team-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Team'}
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Basic Information</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Team Name *</Label><Input value={form.team_name} onChange={e => u('team_name', e.target.value)} data-testid="team-name-input" /></div>
            <div><Label>Team Code</Label><Input value={form.team_code} onChange={e => u('team_code', e.target.value)} placeholder="e.g. SIL-TEAM-01" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Team Type</Label>
              <Select value={form.team_type} onValueChange={v => u('team_type', v)}>
                <SelectTrigger data-testid="team-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TEAM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Team Leader</Label>
              <Select value={form.leader_staff_id} onValueChange={v => u('leader_staff_id', v)}>
                <SelectTrigger data-testid="leader-select"><SelectValue placeholder="Select leader" /></SelectTrigger>
                <SelectContent>{staff.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={e => u('description', e.target.value)} rows={3} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Scope & Linking</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>State</Label>
              <Select value={form.state_id} onValueChange={v => u('state_id', v)}>
                <SelectTrigger><SelectValue placeholder="Org-wide" /></SelectTrigger>
                <SelectContent>{states.map(s => <SelectItem key={s.code} value={s.code}>{s.code} - {s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Branch</Label>
              <Select value={form.branch_id} onValueChange={v => u('branch_id', v)}>
                <SelectTrigger><SelectValue placeholder="Org-wide" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {(form.team_type === 'SIL_HOUSE_TEAM' || form.team_type === 'ACCOMMODATION_TEAM') && (
            <div><Label>Linked House</Label>
              <Select value={form.linked_house_id} onValueChange={v => u('linked_house_id', v)}>
                <SelectTrigger><SelectValue placeholder="Select house" /></SelectTrigger>
                <SelectContent>{houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name || h.house_name || h.id}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Notification Preferences</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {[
            ['shift_notifications', 'Shift Availability Notifications', 'Notify team when open shifts are available'],
            ['participant_notifications', 'Participant Notifications', 'Notify team on participant alerts and updates'],
            ['house_notifications', 'House Notifications', 'Notify team on house incidents and updates'],
            ['messaging_enabled', 'Team Messaging', 'Enable in-app messaging for this team'],
          ].map(([key, label, desc]) => (
            <div key={key} className="flex items-center justify-between py-2">
              <div><p className="text-sm font-medium">{label}</p><p className="text-xs text-muted-foreground">{desc}</p></div>
              <Switch checked={form[key]} onCheckedChange={v => u(key, v)} />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
